import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useGeolocation } from '../hooks/useGeolocation';
import { api } from '../lib/api';
import { getStoredToken } from '../lib/auth';
import { APP_VERSION, BUILD_DATE } from '../lib/version';
import type { Bus } from '../lib/types';

const SOS_REQUIRED_PRESSES = 3;
const SOS_WINDOW_MS = 2000; // 3 taps within 2 seconds

const isNative = Capacitor.isNativePlatform();

// Native GPS plugin — sends GPS from Java, not JavaScript
const NativeGps = isNative
  ? registerPlugin<{
      startTracking(opts: { authToken: string; busId: string; serverUrl: string }): Promise<{ started: boolean }>;
      stopTracking(): Promise<{ stopped: boolean }>;
    }>('NativeGps')
  : null;

interface GpsSenderProps {
  onLogout: () => void;
  userName: string;
}

export function GpsSender({ onLogout, userName }: GpsSenderProps) {
  const SEND_INTERVAL = 30; // seconds (used for browser mode only)
  const [bus, setBus] = useState<Bus | null>(null);
  const [tracking, setTracking] = useState(false);
  const [sendCount, setSendCount] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastSentAt, setLastSentAt] = useState<string>('--');
  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);
  const [loading, setLoading] = useState(true);
  const [nativeActive, setNativeActive] = useState(false);

  // SOS triple-press state
  const [sosTaps, setSosTaps] = useState(0);
  const [sosActive, setSosActive] = useState(false);
  const [sosSending, setSosSending] = useState(false);
  const sosTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const geo = useGeolocation(tracking && !nativeActive); // Only use JS geo in browser mode
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load driver's assigned bus
  useEffect(() => {
    api.getBuses().then(({ buses }) => {
      if (buses.length > 0) {
        setBus(buses[0]);
      } else {
        setLastError('No bus assigned to you. Contact admin.');
      }
    }).catch((err) => {
      setLastError(err.message);
    }).finally(() => setLoading(false));
  }, []);

  // Browser mode: send GPS data via JavaScript
  const sendPosition = useCallback(async () => {
    if (!geo.lat || !geo.lng || !bus) return;

    const token = getStoredToken();
    if (!token) return;

    try {
      const res = await fetch(`/api/gps/${bus.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          lat: geo.lat,
          lng: geo.lng,
          speed: geo.speed,
          heading: geo.heading,
          accuracy: geo.accuracy,
        }),
        keepalive: true,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setLastError((body as { error?: string }).error || `HTTP ${res.status}`);
      } else {
        setLastError(null);
        setSendCount((c) => c + 1);
        setLastSentAt(new Date().toLocaleTimeString());
      }
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Send failed');
    }
  }, [geo.lat, geo.lng, geo.speed, geo.heading, geo.accuracy, bus]);

  // Browser mode: fixed interval sender
  useEffect(() => {
    if (tracking && !nativeActive) {
      if (geo.lat) sendPosition();
      intervalRef.current = setInterval(() => {
        sendPosition();
      }, SEND_INTERVAL * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tracking, nativeActive, sendPosition]);

  // Wake Lock (browser mode)
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);
      }
    } catch { /* not available */ }
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      await wakeLock.release();
      setWakeLock(null);
    }
  };

  const handleStart = async () => {
    if (!bus) {
      setLastError('No bus assigned');
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setLastError('Not authenticated');
      return;
    }

    // Native mode: start Java foreground service (GPS runs in native code)
    if (isNative && NativeGps) {
      try {
        await NativeGps.startTracking({
          authToken: token,
          busId: bus.id,
          serverUrl: 'https://track.lalatendu.info',
        });
        setNativeActive(true);
        setTracking(true);
        setLastError(null);
        setSendCount(0);
        return;
      } catch (err) {
        console.warn('Native GPS failed, falling back to JS:', err);
        // Fall through to browser mode
      }
    }

    // Browser mode fallback
    setTracking(true);
    setSendCount(0);
    setLastError(null);
    requestWakeLock();
  };

  const handleStop = async () => {
    if (isNative && NativeGps && nativeActive) {
      try {
        await NativeGps.stopTracking();
      } catch (err) {
        console.warn('Stop native GPS failed:', err);
      }
      setNativeActive(false);
    }
    setTracking(false);
    releaseWakeLock();
  };

  const handleSOSTap = () => {
    if (sosActive || sosSending || !bus) return;
    const newTaps = sosTaps + 1;
    setSosTaps(newTaps);

    // Reset tap counter after window expires
    if (sosTapTimerRef.current) clearTimeout(sosTapTimerRef.current);
    sosTapTimerRef.current = setTimeout(() => setSosTaps(0), SOS_WINDOW_MS);

    if (newTaps >= SOS_REQUIRED_PRESSES) {
      setSosTaps(0);
      if (sosTapTimerRef.current) clearTimeout(sosTapTimerRef.current);
      triggerSOS();
    }
  };

  const triggerSOS = async () => {
    if (!bus) return;
    setSosSending(true);
    try {
      let lat: number | undefined;
      let lng: number | undefined;
      if (geo.lat && geo.lng) {
        lat = geo.lat;
        lng = geo.lng;
      } else {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 3000 });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch { /* no location */ }
      }
      await api.sendSOS({ bus_id: bus.id, latitude: lat, longitude: lng, message: `Emergency SOS from driver ${userName}` });
      setSosActive(true);
    } catch {
      setLastError('SOS failed — try again');
    } finally {
      setSosSending(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading bus assignment...</div>;
  }

  return (
    <div className="sender-page">
      <div className="sender-card">
        <div className="sender-header">
          <div>
            <h1>🚌 GPS Sender</h1>
            <p className="sender-subtitle">Driver: {userName}</p>
          </div>
          <div className="sender-header-right">
            <span className="version-tag">{APP_VERSION}</span>
            <button className="btn-logout" onClick={onLogout}>Logout</button>
          </div>
        </div>

        {/* Bus info */}
        {bus ? (
          <div className="bus-info">
            <div className="bus-info-number">{bus.bus_number}</div>
            <div className="bus-info-plate">{bus.license_plate || 'No plate'}</div>
          </div>
        ) : (
          <div className="form-error">No bus assigned to your account. Contact school admin.</div>
        )}

        {/* Controls */}
        {bus && !tracking && (
          <button className="btn-primary btn-start" onClick={handleStart}>
            Start Tracking
          </button>
        )}
        {tracking && (
          <button className="btn-danger btn-stop" onClick={handleStop}>
            Stop Tracking
          </button>
        )}

        {/* Status */}
        {tracking && (
          <div className="sender-status">
            <div className="status-row">
              <span>Mode</span>
              <span className={nativeActive ? 'status-active' : 'status-waiting'}>
                {nativeActive ? 'Native GPS Service (Background OK)' : 'Browser (Keep Screen On)'}
              </span>
            </div>
            {nativeActive ? (
              <>
                <div className="status-row">
                  <span>Status</span>
                  <span className="status-active">Running in Native Service</span>
                </div>
                <div className="status-row">
                  <span>Interval</span>
                  <span>Every 30s (native)</span>
                </div>
                <div className="status-row">
                  <span>Background</span>
                  <span className="status-active">Screen off OK</span>
                </div>
              </>
            ) : (
              <>
                <div className="status-row">
                  <span>Status</span>
                  <span className={geo.lat ? 'status-active' : 'status-waiting'}>
                    {geo.lat ? 'Sending' : 'Acquiring GPS...'}
                  </span>
                </div>
                <div className="status-row">
                  <span>Position</span>
                  <span>
                    {geo.lat ? `${geo.lat.toFixed(6)}, ${geo.lng?.toFixed(6)}` : '--'}
                  </span>
                </div>
                <div className="status-row">
                  <span>Speed</span>
                  <span>
                    {geo.speed ? `${(geo.speed * 3.6).toFixed(1)} km/h` : '0 km/h'}
                  </span>
                </div>
                <div className="status-row">
                  <span>Accuracy</span>
                  <span>
                    {geo.accuracy ? `${Math.round(geo.accuracy)}m` : '--'}
                  </span>
                </div>
                <div className="status-row">
                  <span>Interval</span>
                  <span>Every {SEND_INTERVAL}s</span>
                </div>
                <div className="status-row">
                  <span>Updates sent</span>
                  <span>{sendCount}</span>
                </div>
                <div className="status-row">
                  <span>Last sent</span>
                  <span>{lastSentAt}</span>
                </div>
                <div className="status-row">
                  <span>Wake Lock</span>
                  <span>{wakeLock ? 'Active' : 'Inactive'}</span>
                </div>
              </>
            )}
          </div>
        )}

        {lastError && <div className="form-error">{lastError}</div>}
        {geo.error && !nativeActive && <div className="form-error">GPS: {geo.error}</div>}
      </div>

      {/* Floating SOS Button — triple press to activate */}
      {bus && (
        <button
          className={`sos-floating-btn ${sosTaps > 0 ? 'sos-tapping' : ''} ${sosActive ? 'sos-triggered' : ''}`}
          onClick={handleSOSTap}
          disabled={sosActive || sosSending}
        >
          {sosSending ? '...' : sosActive ? 'SOS SENT' : sosTaps > 0 ? `SOS (${SOS_REQUIRED_PRESSES - sosTaps})` : 'SOS'}
        </button>
      )}

      {/* SOS Confirmation Overlay */}
      {sosActive && (
        <div className="sos-overlay">
          <div className="sos-overlay-content">
            <div className="sos-check-icon">✓</div>
            <h2>SOS Alert Sent!</h2>
            <p>All school admins have been notified.</p>
            <p className="sos-overlay-sub">Help is on the way.</p>
            <button className="btn-primary" onClick={() => setSosActive(false)} style={{ marginTop: 20 }}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
